import AppLayout from "@/components/layout/AppLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useEffect, useMemo, useState } from "react";
import { api } from "@/api/http";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";


type ClienteFormState = {
  nome: string;
  tipo: "PF" | "PJ";
  documento: string;
  ie: string;
  email: string;
  telefone: string;
  cep: string;
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  uf: string;
};

const initialState: ClienteFormState = {
  nome: "",
  tipo: "PF",
  documento: "",
  ie: "",
  email: "",
  telefone: "",
  cep: "",
  logradouro: "",
  numero: "",
  complemento: "",
  bairro: "",
  cidade: "",
  uf: "",
};

export default function ClienteForm() {
  const { id } = useParams(); // <- vem do /clientes/:id/editar
  const isEdit = useMemo(() => !!id, [id]);

  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [loadingCliente, setLoadingCliente] = useState(false);
  const [form, setForm] = useState<ClienteFormState>(initialState);

  const [cepLoading, setCepLoading] = useState(false);
  const [cepError, setCepError] = useState<string | null>(null);

  function onlyDigits(v: string) {
    return (v || "").replace(/\D/g, "");
  }

  async function fetchCep(cep8: string) {
    setCepLoading(true);
    setCepError(null);

    try {
      const res = await fetch(`https://viacep.com.br/ws/${cep8}/json/`);
      const data = await res.json();

      if (data?.erro) {
        setCepError("CEP não encontrado");
        return;
      }

      setForm((prev) => ({
        ...prev,
        logradouro: data?.logradouro ?? prev.logradouro,
        bairro: data?.bairro ?? prev.bairro,
        cidade: data?.localidade ?? prev.cidade,
        uf: data?.uf ?? prev.uf,
        // complemento vem do usuário, não mexe
      }));
    } catch {
      setCepError("Erro ao consultar CEP");
    } finally {
      setCepLoading(false);
    }
  }


  function handleChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  async function loadCliente(clienteId: string) {
    setLoadingCliente(true);
    try {
      const { data } = await api.get(`/api/clientes/${clienteId}`);
      // backend retorna { cliente: {...} }
      const c = data?.cliente;

      setForm({
        nome: c?.nome ?? "",
        tipo: (c?.tipo ?? "PF") as "PF" | "PJ",
        documento: c?.documento ?? "",
        ie: c?.ie ?? "",
        email: c?.email ?? "",
        telefone: c?.telefone ?? "",
        cep: c?.cep ?? "",
        logradouro: c?.logradouro ?? "",
        numero: c?.numero ?? "",
        complemento: c?.complemento ?? "",
        bairro: c?.bairro ?? "",
        cidade: c?.cidade ?? "",
        uf: c?.uf ?? "",
      });
    } catch (err) {
      alert("Não foi possível carregar o cliente para edição.");
      navigate("/clientes");
    } finally {
      setLoadingCliente(false);
    }
  }

  useEffect(() => {
    if (id) loadCliente(id);
    else setForm(initialState);
  }, [id]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);

    try {
      if (isEdit && id) {
        await api.put(`/api/clientes/${id}`, form);
        toast.success("Cliente atualizado com sucesso!");

      } else {
        await api.post("/api/clientes", form);
        toast.success("Cliente cadastrado com sucesso!");

      }

      navigate("/clientes");
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Erro ao salvar cliente";
      alert(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <AppLayout>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-slate-900">
            {isEdit ? "Editar Cliente" : "Cadastro de Cliente"}
          </h1>

          <Button type="button" variant="outline" onClick={() => navigate("/clientes")}>
            Voltar
          </Button>
        </div>

        {loadingCliente ? (
          <div className="bg-white p-6 rounded-xl shadow-sm">
            Carregando dados do cliente...
          </div>
        ) : (
          <>
            {/* Dados principais */}
            <section className="bg-white p-6 rounded-xl shadow-sm space-y-4">
              <h2 className="font-semibold text-slate-700">Dados do Cliente</h2>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  name="nome"
                  placeholder="Nome / Razão Social"
                  value={form.nome}
                  onChange={handleChange}
                />

                <select
                  name="tipo"
                  value={form.tipo}
                  onChange={handleChange}
                  className="h-10 rounded-md border border-slate-300 px-3 text-sm"
                >
                  <option value="PF">Pessoa Física</option>
                  <option value="PJ">Pessoa Jurídica</option>
                </select>

                <Input
                  name="documento"
                  placeholder="CPF ou CNPJ"
                  value={form.documento}
                  onChange={handleChange}
                />

                <Input
                  name="ie"
                  placeholder="Inscrição Estadual (opcional)"
                  value={form.ie}
                  onChange={handleChange}
                />

                <Input
                  name="email"
                  placeholder="Email"
                  value={form.email}
                  onChange={handleChange}
                />

                <Input
                  name="telefone"
                  placeholder="Telefone / WhatsApp"
                  value={form.telefone}
                  onChange={handleChange}
                />
              </div>
            </section>

            {/* Endereço */}
            <section className="bg-white p-6 rounded-xl shadow-sm space-y-4">
              <h2 className="font-semibold text-slate-700">
                Endereço (Nota Fiscal)
              </h2>

              <div className="grid grid-cols-3 gap-4">
                <Input
                  name="cep"
                  placeholder="CEP"
                  value={form.cep}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const digits = onlyDigits(raw).slice(0, 8);

                    setForm((prev) => ({ ...prev, cep: digits }));

                    if (digits.length === 8) {
                      fetchCep(digits);
                    } else {
                      setCepError(null);
                    }
                  }}
                />
                {cepLoading && (
                  <p className="text-xs text-slate-500">Buscando endereço pelo CEP...</p>
                )}

                {cepError && (
                  <p className="text-xs text-red-600">{cepError}</p>
                )}


                <Input name="cidade" placeholder="Cidade" value={form.cidade} onChange={handleChange} />
                <Input name="uf" placeholder="UF" value={form.uf} onChange={handleChange} />

                <Input
                  className="col-span-2"
                  name="endereco"
                  placeholder="Endereço"
                  value={form.logradouro}
                  onChange={handleChange}
                />

                <Input
                  name="numero"
                  placeholder="Número"
                  value={form.numero}
                  onChange={handleChange}
                />

                <Input
                  className="col-span-3"
                  name="complemento"
                  placeholder="Complemento"
                  value={form.complemento}
                  onChange={handleChange}
                />

                <Input
                  className="col-span-3"
                  name="bairro"
                  placeholder="Bairro"
                  value={form.bairro}
                  onChange={handleChange}
                />
              </div>
            </section>

            {/* Ações (rodapé fixo) */}
            <div className="flex justify-end gap-3 sticky bottom-0 bg-slate-100 py-4">
              <Button type="button" variant="outline" onClick={() => navigate("/clientes")}>
                Cancelar
              </Button>

              <Button disabled={loading}>
                {loading ? "Salvando..." : isEdit ? "Salvar Alterações" : "Salvar Cliente"}
              </Button>
            </div>
          </>
        )}
      </form>
    </AppLayout>
  );
}
